import { OnInit, Component, Output, EventEmitter, ViewChild, Input, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../../shared/services/alert.service';
import { DeviceService } from '../../../../shared/services/device.service';
import { Device } from '../../../../shared/model/device';
import { ErrorService } from '../../../../shared/services/error.service';
import { CareHome } from 'src/app/shared/model/carehome';
import { CareHomeService } from 'src/app/shared/services/carehome.service';
import { InterceptorNotificationService } from 'src/app/shared/services/interceptor.notification.service';
import { UserSessionService } from '../../../../shared/services/user-session.service';
import { FIRMWARE_VERSION_OPTIONS } from '../../../../shared/config/device-constants';
import Roles from '../../../../shared/model/user/Roles';

@Component({
    selector: 'carehome-device-create',
    templateUrl: 'carehome-device-create.html',
    styleUrls: ['carehome-device-create.component.scss']
})
export class CareHomeDeviceCreateComponent implements OnInit {
    @ViewChild('f', { static: true }) form: NgForm;
    @ViewChild('deviceIdInput', { static: true }) deviceIdInput: ElementRef;
    @ViewChild('macAddress', { static: true }) macAddress: ElementRef;
    @ViewChild('closeModal', { static: true }) closeModal: ElementRef;
    @ViewChild('multiDevices') multiDevices: ElementRef;
    @ViewChild('status', { static: true }) status: ElementRef;
    @ViewChild('careHomeId', { static: true }) careHomeId: ElementRef;
    @Output() deviceEmitter: EventEmitter<Device> = new EventEmitter<Device>();

    model: Device;
    modaltitle = '';
    btnText = '';
    isEdit: boolean;
    isUpdating = false;
    carehomeList: CareHome[] = [];
    deviceIdDisabled: boolean = false;
    multiDevicesEnabled: boolean = false;
    isGlobalAdmin = false;
    firmwareVersionOptions = FIRMWARE_VERSION_OPTIONS;

    constructor(private deviceService: DeviceService,
        private alertService: AlertService,
        private translate: TranslateService,
        private errorService: ErrorService,
        private careHomeService: CareHomeService,
        private notificationService: InterceptorNotificationService,
        private userSessionService: UserSessionService) {
    }

    ngOnInit(): void {
        this.model = new Device();
        this.isGlobalAdmin = this.userSessionService.userHasRole(Roles.Admin);
    }

    initNewForm() {
        this.getCarehgomeList();
        this.alertService.clear();
        this.form.resetForm();
        this.model = new Device();
        this.model.CareHomeId = null;
        this.form.control.patchValue({
            carehomeId: null,
            deviceName: null,
            deviceId: null,
            status: true,
            multi: false,
            firmwareVersion: null
        });
        this.isEdit = false;

        this.translate.get('device.addButton').subscribe((result: string) => {
            this.btnText = result;
        });

        this.translate.get('device.addnewDeviceTitle').subscribe((result: string) => {
            this.modaltitle = result;
        });
    }

    initEditForm(id: string, careHomeId: string) {
        this.alertService.clear();
        this.model.Id = id;
        this.model.CareHomeId = careHomeId;
        this.isEdit = true;
        this.getDeviceById();
        this.getCarehgomeList();

        this.translate.get('device.saveButton').subscribe((result: string) => {
            this.btnText = result;
        });

        this.translate.get('device.editDeviceTitle').subscribe((result: string) => {
            this.modaltitle = result;
        });
    }

    getDeviceById() {
        this.deviceService.getCareHomeDeviceById(this.model.Id).subscribe(data => {
            this.model = data.body;
        }, error => {
            this.errorService.handleError(error);
        });
    }

    getCarehgomeList(): void {
        this.careHomeService.getAll().subscribe(data => {
            this.carehomeList = data.body;
        }, error => {
            this.errorService.handleError(error);
        });
    }

    saveDevice(): void {
        this.alertService.clear();
        this.upsert(this.model);
    }

    upsert(device: Device) {
        this.deviceService.upsert(device).subscribe(data => {
            if (data.body != null) {
                this.deviceEmitter.emit(data.body);
                this.translate.get('device.savedSuccess', { name: this.model.Name }).subscribe((res: string) => {
                    this.alertService.success(res);
                    if (!this.isEdit && this.multiDevicesEnabled) {
                        this.resetInputs();
                    } else {
                        this.closeModal.nativeElement.click();
                    }
                });
            } else {
                this.translate.get('device.deviceExists', { name: this.model.Name }).subscribe((res: string) => {
                    this.alertService.error(res);
                });
            }
        }, error => {
            if (error.error.statusCode == 409) {
                this.notificationService.handleErrorMessage("Device already Linked to a resident");
            } else {
                this.errorService.handleError(error);
            }
        });
    }

    resetInputs() {
        this.macAddress.nativeElement.focus();
        const careHomeId = this.model.CareHomeId;
        const firmwareVersion = this.model.FirmwareVersion;

        this.form.resetForm();
        this.form.control.patchValue({
            carehomeId: careHomeId,
            deviceName: null,
            deviceId: null,
            status: true,
            multi: true,
            firmwareVersion: firmwareVersion
        });

        this.deviceIdDisabled = false;
    }

    search() {
        this.deviceService
            .search(this.model.Name)
            .subscribe({
                next: (data) => {
                    this.deviceIdDisabled = true;
                    this.model.Id = data.body.MACAddress;
                    setTimeout(() => {
                        this.deviceIdInput.nativeElement.value = this.model.Id;
                    }, 0);
                },
                error: (error) => {
                    console.log(error);
                    this.deviceIdDisabled = false;
                    this.model.Id = null;
                    setTimeout(() => {
                        this.deviceIdInput.nativeElement.focus();
                    }, 0);
                    console.error(error);
                }
            });
    }
}
